import * as OWL from '../owl';
import { Carrier } from '../owl';
import { OccupancyType } from '../parrot';
import {
  Agency,
  AgentInfo,
  DeclarationPage,
  PagedOwlResponse,
  LineType,
} from '../types';

export interface OwlClientInput {
  args?: Record<string, any>;
  query?: Record<string, string | boolean | number | string[] | undefined>;
  payload?: any;
}

type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

export type OwlApiHandler = (args: OwlClientInput) => Promise<any>;

export type AgencyInput = Omit<
  Agency,
  'id' | 'slugPrefix' | 'logoUrl' | 'isActive' | 'freeTrial'
> & {
  logo?: Blob;
};

export type ReferralPartnerInput = {
  referralPartnerName?: string;
  referralPartnerJobTitle?: string;
  referralPartnerCompany?: string;
  referralPartnerHeadshot?: Blob;
  referralPartnerLogo?: Blob;
};

export type AgentInput = Omit<
  AgentInfo,
  | 'id'
  | 'externalIdentifier'
  | 'profilePictureUrl'
  | 'slug'
  | 'isActive'
  | 'onboardingComplete'
  | 'publicSettings'
  | 'intercomUserHash'
  | 'completedPersonaSurvey'
  | 'onboardingCompletedAt'
> & {
  profilePicture?: Blob;
  //** owl will auto generate an agents slug, ONLY if one does not exist already!! */
  autoGenerateSlug?: boolean;
};

export interface AutomationSummary {
  logs: string[];
  policy_ids: string[];
  links?: Record<string, string>;
}

export interface PolicyHolder {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

export interface AppDriver {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  usStateCode: string;
  driverLicenseCode: string;
}

export interface PolicyDiscount {
  name?: string;
  description?: string;
  type?: string;
  amount?: string | number;
}

export interface PolicyCoverageDeductibles {
  amount?: string;
  description?: string;
}

export interface PolicyCoverageLimit {
  limitPerType?: string;
  amount?: string;
  description?: string;
  max?: string;
}

export interface PolicyCoverage {
  id: number;
  name: string;
  description: string;
  premium: string;
  limits: PolicyCoverageLimit[];
  deductibles: PolicyCoverageDeductibles[];
  limitPerPerson: string;
  limitPerIncident: string;
  deductible: string;
  isDeclined?: null | boolean;
}

export interface MiscDwellingInformation {
  squareFootage: string | null;
}

export interface UpdateDwellingPayload
  extends BasicAddressDetails,
    BasicAnimalDetails,
    BasicRoofDetails,
    BasicPoolDetails,
    MiscDwellingInformation {
  uuid: string;
}

export enum DataSource {
  Automation = 'carrier',
  ClientEntry = 'client_entry',
  CarrierDataExtraction = 'carrier_data_extraction',
  AIExtracted = 'ai_extracted',
}

export interface DwellingInformation {
  uuid: string;
  nickname?: string;
  occupancyType: OccupancyType | null;
  yearsAtResidence?: number;
  address: BasicAddressDetails;
  roof: BasicRoofDetails;
  animals: BasicAnimalDetails;
  pool: BasicPoolDetails;
  misc: MiscDwellingInformation;
  coverages: PolicyCoverage[];
  discounts: PolicyDiscount[];
  source: DataSource;
}

export type DwellingProperties = BasicAnimalDetails &
  BasicRoofDetails &
  BasicAddressDetails &
  BasicPoolDetails &
  DwellingInformation;

export type DwellingSectionProperties = keyof Omit<
  DwellingProperties,
  'DwellingInformation'
>;

export type DwellingSections = keyof Omit<
  DwellingInformation,
  'squareFootage' | 'uuid'
>;

// export interface PolicyAddress {
//   city: string;
//   postal: string;
//   street: string;
//   street2: string;
//   usStateCode: string;
//   roofAge?: number;
//   roofMaterial?: string;
//   swimmingPool?: boolean;
//   poolFence?: boolean;
//   uuid: string;
// }

export interface BasicAddressDetails {
  street: string | null;
  street2: string | null;
  city: string | null;
  usStateCode: string | null;
  postal: string | null;
  nickname: string | null;
}

export interface BasicRoofDetails {
  roofAge: number | null;
  roofMaterial: string | null;
}

export interface BasicAnimalDetails {
  haveDogs: boolean | null;
  dogs: string[] | null;
  dangerousOrExoticAnimal: boolean | null;
  animalThatCausedInjury: boolean | null;
}

export interface BasicPoolDetails {
  swimmingPool: boolean | null;
  poolType: string | null;
  poolFence: boolean | null;
}

// export type BasicAddress = Omit<PolicyAddress, 'uuid'>;

export interface PolicyDwelling extends BasicAddressDetails {
  coverages: PolicyCoverage[];
  animalThatCausedInjury?: boolean;
  dangerousOrExoticAnimal?: boolean;
  dogs?: string[];
  haveDogs?: boolean;
  poolFence: boolean;
  roofAge: number;
  roofMaterial: string;
  squareFootage: number;
  occupancyType?: string;
  swimmingPool: boolean;
  uuid: string;
}

export interface PolicyVehicle {
  uuid: string;
  year: number;
  make: string;
  model: string;
  vin: string;
  name?: string;
  garagingAddress: BasicAddressDetails;
  coverages: PolicyCoverage[];
  discounts: PolicyDiscount[];
  source?: DataSource;
  policies?: string[];
}

export interface PolicyType {
  id: number;
  name: string;
}

export interface OwlSubmissionPolicy {
  policyType: PolicyType;
  cancellationDate: string;
  cancellationReason: string;
  mailingAddress: BasicAddressDetails;
  createdAt: string;
  effectiveDate: string;
  expirationDate: string;
  isThirdPartyCarrier: number;
  policyId: string;
  premium: string;
  policyHolder: PolicyHolder;
  coverages: PolicyCoverage[];
  drivers: unknown[];
  dwellings: PolicyDwelling[];
  vehicles: PolicyVehicle[];
  thirdPartyCarrierName: string;
  uuid: string;
}

export interface UpdateDeclarationPagePayload {
  policyId?: string;
  policyType?: string;
  premium?: number | string;
  /** YYYY-MM-DD */
  policyStartDate?: string;
  /** YYYY-MM-DD */
  policyEndDate?: string;
  isThirdPartyCarrier?: boolean;
  thirdPartyCarrierName?: string;
}

export interface SplitSubmissionPayload {
  declarationPageUuids: string[];
  firstName?: string;
  lastName?: string;
}

export interface SplitSubmissionResponse {
  clientUuid: string;
  clientSubmissionUuid: string;
}

export interface OwlAddPolicyData {
  policyId: string;
  policyType: string;
  mailingAddress?: Partial<BasicAddressDetails>;
  policyHolder?: Partial<PolicyHolder>;
  coApplicant?: Partial<PolicyHolder>;
  premium?: string;
  effectiveDate: string | null;
  expirationDate?: string | null;
  cancellationDate?: string | null;
  vehicles: DeepPartial<PolicyVehicle>[];
  drivers: Partial<AppDriver>[];
  dwellings: DeepPartial<PolicyDwelling>[];
  coverages: Partial<PolicyCoverage>[];
  discounts: Partial<PolicyDiscount>[];
  isThirdPartyCarrier?: boolean;
  thirdPartyCarrierName?: string;
}

export enum Scenario {
  NoPoliciesAvailable = 'no_policies_available',
  ThirdPartyDocsNotAvailable = 'third_party_documents_not_available',
  AutomationIncomplete = 'automation_incomplete',
}

export interface OwlSubmissionInfo {
  uuid: string;
  username: string;
  password: string;
  additionalFields: {
    zip: string | null;
    state: string | null;
  };
  carrierSlug: string;
  status: OWL.SubmissionStatus;
  humanReadableStatus: string;
  credentialsStatus: 'valid';
  humanReadableCredentialStatus: 'Valid';
  isMfaRequired: boolean;
  messageForAgent: string | null;
  metadata: null;
  automationSummary: AutomationSummary;
  carrier: Carrier;
  declarationPages: DeclarationPage[];
  policies: OwlSubmissionPolicy[];
  submittedAt: string;
  submittedAtHuman: string;
  approvedAt: string;
  approvedBy: string | null;
}

export interface ClientSubmissionQueryParams {
  query: {
    page?: number;
    autoApproved?: number;
    carrierSlugs?: string[];
    policyTypeIds?: string[];
    policyTypeIdsManual?: string[];
    policyTypesIdsAutomatic?: string[];
    dateRangeStart?: string;
    dateRangeEnd?: string;
    perPage?: number;
    automatedMfa?: number;
  };
}

export type SubmissionListResponse = PagedOwlResponse<CarrierSubmission[]>;

export interface ModifiedCarrierSubmission extends CarrierSubmission {
  ticketCount: number;
  statuses: JiraStatuses[];
}

export interface CarrierSubmission {
  client_submission_uuid: string;
  auto_approved: boolean;
  carrier_name: string;
  date: string;
  is_mfa: boolean;
  automated_mfa: boolean;
  policy_types_manually_retrieved: string; //comma delimited list
  policy_types_automatically_retrieved: string; //comma delimited list
  message_for_agent: string;
  automation_summary: AutomationSummary;
  agent_slug?: string;
}

export type SortDirection = 'asc' | 'desc';

export type PolicyTypeResponse = PagedOwlResponse<PolicyType[]>;

export interface PolicyType {
  id: number;
  name: string;
}

export enum JiraPriorities {
  highest = 'Highest',
  high = 'High',
  medium = 'Medium',
  low = 'Low',
  lowest = 'Lowest',
}

export enum JiraStatuses {
  Backlog = 'Backlog',
  SelectedForDevelopment = 'Selected for Development',
  CodeReview = 'Code Review',
  InProgress = 'In Progress',
  ReadyForRelease = 'Ready for Release',
  ReadyForReview = 'Ready for Review',
  Deployed = 'Deployed',
  Done = 'Done',
  WontDo = "Won't Do",
}

export interface AddSubmissionsToTicketPayload {
  ticket: string;
  clientSubmissionUuids: string[];
}

export interface CreateTicketPayload {
  summary: string;
  clientSubmissionUuids: string[];
  description: string;
}

export interface CreateTicketResponse {
  issueKey: string;
  type: string | null;
  summary: string | null;
  description: string | null;
}

export interface SubmissionQuery extends Omit<OwlClientInput, 'payload'> {
  autoApproved?: number;
  carrierSlugs?: string[];
  policyTypeIds?: string[];
  policyTypeIdsManual?: string[];
  policyTypesIdsAutomatic?: string[];
  dateRangeStart?: string;
  dateRangeEnd?: string;
  perPage?: number;
  automatedMfa?: number;
  page?: number;
  sortBy?: string;
  sortDirection?: SortDirection;
  ticketStatus?: string[];
}

export interface UserTrackingPayload {
  slug?: string;
  trackingUuid?: string;
  insuranceLine: LineType;
  iframe: boolean;
  clientId?: string;
  smsId?: string;
  emailId?: string;
  customLinkId?: string;
  device: 'mobile' | 'tablet' | 'desktop' | null;
}

export interface UpdateClientPolicyPayload {
  source: DataSource;
  mailingAddress: {
    nickname?: string;
    street?: string;
    street2?: string;
    city?: string;
    usStateCode?: string;
    postal?: string;
  };
  drivers: {
    firstName: string;
    lastName?: string;
    dateOfBirth?: string;
    usStateCode?: string;
    driverLicenseCode?: string;
  }[];
  vehicles: {
    name?: string;
    make?: string;
    model?: string;
    year?: string;
    vin?: string;
    primaryUse?: string;
  }[];
}

// policyId lives ONLY on the envelope (AddClaimsPayload), not per ClaimRecord —
// claims are POSTed one-policy-per-call, so repeating policyId per record was redundant.
/**
 * One scraped insurance claim within an AddClaimsPayload batch (all claims in a
 * batch belong to the single policy named by AddClaimsPayload.policyId).
 * `claimId` is the carrier's claim number when shown; owl generates a uuid when omitted.
 */
export interface ClaimRecord {
  claimId?: string;
  date?: string; // ISO date
  status?: string; // normalized; carrier's raw value goes in metadata.rawStatus
  amount?: string;
  metadata?: Record<string, unknown>;
}

export interface AddClaimsPayload {
  policyId: string; // carrier policy number; owl resolves to the FK for every claim row
  claims: ClaimRecord[];
}
